import { z } from 'zod';
/**
 * Kontaktdatenbank (ADR-0016).
 *
 * Zwei Sorten, eine Gestalt: `eigene` gehören einer Person, `zentral` dem
 * Unternehmen. Lesen dürfen alle beides, pflegen darf jeder seine eigenen —
 * die zentralen nur, wer die Rolle dafür hat.
 */
export const kontaktbereiche = ['eigene', 'zentral'];
/** Die Felder, die ein Kontakt trägt — auch die Zielspalten des CSV-Imports. */
export const kontaktfelder = [
    'firma',
    'nachname',
    'vorname',
    'email',
    'mobil',
    'buero',
    'position',
    'bemerkung',
];
/** Was in der Oberfläche über einem Feld steht. Eine Quelle für alle Listen. */
export const KONTAKTFELDNAMEN = {
    firma: 'Firma',
    nachname: 'Name',
    vorname: 'Vorname',
    email: 'E-Mail',
    mobil: 'Handy',
    buero: 'Büro',
    position: 'Position',
    bemerkung: 'Bemerkung',
};
const text = z.string().trim().max(200);
const langerText = z.string().trim().max(2000);
export const kontaktSchema = z.object({
    id: z.string().uuid(),
    bereich: z.enum(kontaktbereiche),
    firma: z.string().nullable(),
    vorname: z.string().nullable(),
    nachname: z.string().nullable(),
    email: z.string().nullable(),
    position: z.string().nullable(),
    bemerkung: z.string().nullable(),
    mobil: z.string().nullable(),
    buero: z.string().nullable(),
    angelegtAm: z.string(),
    geaendertAm: z.string(),
});
export const kontaktListeSchema = z.object({ kontakte: z.array(kontaktSchema) });
/**
 * Was beim Anlegen und Ändern hereinkommt.
 *
 * Leere Zeichenketten werden zu `null`: Ein Feld, das jemand leert, soll leer
 * sein und nicht als leerer Text in der Datenbank stehen — sonst unterscheiden
 * sich „nie ausgefüllt" und „gelöscht" in jeder Abfrage.
 */
const leerZuNull = (feld) => feld.transform((w) => (w === '' ? null : w)).nullable();
export const kontaktEingabeSchema = z
    .object({
    firma: leerZuNull(text).optional(),
    vorname: leerZuNull(text).optional(),
    nachname: leerZuNull(text).optional(),
    email: leerZuNull(text).optional(),
    position: leerZuNull(text).optional(),
    bemerkung: leerZuNull(langerText).optional(),
    mobil: leerZuNull(text).optional(),
    buero: leerZuNull(text).optional(),
})
    .refine((e) => Boolean(e.firma || e.vorname || e.nachname), {
    message: 'Firma oder Name erforderlich',
});
/** `POST /api/kontakte` — dazu der Bereich, in den er gehört. */
export const kontaktAnlegenSchema = z.object({
    bereich: z.enum(kontaktbereiche),
    kontakt: kontaktEingabeSchema,
});
/**
 * Höchstens so viele Zeilen je Import.
 *
 * Nicht, weil mehr nicht ginge, sondern weil ein Import in einer Anfrage
 * durchläuft: Was hier hereinkommt, muss in einen Rumpf passen und in einer
 * Transaktion geschrieben werden können.
 */
export const IMPORT_ZEILEN_MAX = 2000;
export const kontaktImportSchema = z.object({
    bereich: z.enum(kontaktbereiche),
    kontakte: z.array(kontaktEingabeSchema).min(1).max(IMPORT_ZEILEN_MAX),
});
export const kontaktImportErgebnisSchema = z.object({
    angelegt: z.number().int().nonnegative(),
    /** Zeilen, die nichts Brauchbares enthielten — mit ihrer Nummer im CSV. */
    uebersprungen: z.array(z.object({ zeile: z.number().int().positive(), grund: z.string() })),
});
/**
 * Was die Anrufanzeige zu einer Nummer zeigt (ADR-0016, Punkt 2): Name, Firma,
 * Position — und klein die Nummer. `null`, wenn nichts passt.
 */
export const kontakttrefferSchema = z.object({
    name: z.string(),
    firma: z.string().nullable(),
    position: z.string().nullable(),
    bereich: z.enum(kontaktbereiche),
});
export const kontaktAufloesungSchema = z.object({ treffer: kontakttrefferSchema.nullable() });
/** Der Name, wie er in Liste und Anzeige steht: „Nachname, Vorname" oder die Firma. */
export function kontaktname(k) {
    const person = [k.nachname, k.vorname].filter(Boolean).join(', ');
    return person || (k.firma ?? '');
}
//# sourceMappingURL=kontakte.js.map